import { DataProvider, HttpError } from "@refinedev/core";
import axios, { AxiosError } from "axios";
import Session from "supertokens-web-js/recipe/session";
import { ensureSuperTokens } from "@/lib/supertokens";

export const axiosInstance = axios.create();

// Every admin route on the server sits behind the global SuperTokensAuthGuard
// plus AdminGuard, so requests must carry the session. Token transfer is
// header-based (the server pins it), and these interceptors are what attach the
// access token and transparently refresh it on a 401 — without them every call
// goes out anonymous and comes back "No token provided".
ensureSuperTokens();
Session.addAxiosInterceptors(axiosInstance);

// Normalize API errors into Refine's HttpError shape so notifications show the
// server's message + status instead of a bare "Network Error (undefined)".
// immersive-server returns either { success, error: "<msg>", errorType } or
// { error: { type, message } }; Nest's own exceptions return { message }.
function extractMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const body = data as { error?: unknown; message?: unknown };
  if (typeof body.error === "string") {
    return body.error;
  }
  if (body.error && typeof body.error === "object") {
    const inner = (body.error as { message?: unknown }).message;
    if (typeof inner === "string") {
      return inner;
    }
  }
  if (typeof body.message === "string") {
    return body.message;
  }
  return undefined;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const httpError: HttpError = {
      message: extractMessage(error.response?.data) ?? error.message ?? "Something went wrong",
      statusCode: error.response?.status ?? 0,
    };
    return Promise.reject(httpError);
  }
);

export const dataProvider = (apiUrl: string): DataProvider => ({
  getApiUrl: () => apiUrl,

  getList: async ({ resource, pagination, filters }) => {
    const { currentPage = 1, pageSize = 20 } = pagination ?? {};
    // Simple `field eq value` filters are forwarded as query params; anything
    // else is dropped rather than half-applied. The admin endpoints each accept
    // their own named params (`status`, `storyId`, …) and ignore what they do
    // not know, so a filter a resource has no support for is inert rather than
    // an error — but a filter that silently did nothing while the UI showed it
    // as active would be worse, which is why only the shape the server actually
    // understands is sent.
    const filterParams: Record<string, string> = {};
    for (const filter of filters ?? []) {
      if ("field" in filter && filter.operator === "eq" && filter.value != null && filter.value !== "") {
        filterParams[filter.field] = String(filter.value);
      }
    }

    const { data: response } = await axiosInstance.get(
      `${apiUrl}/${resource}`,
      {
        params: {
          page: currentPage,
          limit: pageSize,
          ...filterParams,
        },
      }
    );

    return {
      data: response.data,
      total: response.meta.total,
    };
  },

  getOne: async ({ resource, id }) => {
    const { data: response } = await axiosInstance.get(
      `${apiUrl}/${resource}/${id}`
    );
    return { data: response };
  },

  create: async ({ resource, variables }) => {
    const { data: response } = await axiosInstance.post(
      `${apiUrl}/${resource}`,
      variables
    );
    return { data: response };
  },

  update: async ({ resource, id, variables }) => {
    const { data: response } = await axiosInstance.patch(
      `${apiUrl}/${resource}/${id}`,
      variables
    );
    return { data: response };
  },

  deleteOne: async ({ resource, id }) => {
    const { data: response } = await axiosInstance.delete(
      `${apiUrl}/${resource}/${id}`
    );
    return { data: response };
  },
});